import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { highlightedItemsTracker, TrackedHighlightItem } from '@/lib/services/HighlightedItemsTracker';
import { highlightVisibilityService } from '@/lib/services/HighlightVisibilityService';
import { useTabContext } from '@/contexts/TabContext';
import { getSelectedGame } from '@/lib/stores/unifiedUserStore';
import { useLiveEntityRelationships } from '@/hooks/useLiveEntityRelationships';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { objectsStore } from "@/lib/stores/objectsStore";
import { documentStore } from '@/lib/stores/documentStore';
import { templatesStore } from "@/lib/stores/templatesStore";
import { Library, Tag, FileText, Eye, EyeOff, BookOpen, Layout, Type, ChevronRight } from 'lucide-react';
import { getEntityColor, getEntityTypeLabel } from '@/lib/utils/entityColors';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export default function StructureSidebarHighlights() {
  const { t } = usePlatformTranslation();
  const [highlightedItems, setHighlightedItems] = useState<TrackedHighlightItem[]>([]);
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const [hiddenItems, setHiddenItems] = useState<Set<string>>(new Set());
  const { openTab } = useTabContext();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { selectedGame } = useSimplifiedUserData();

  // Determine current entity from route
  const getCurrentEntity = () => {
    const path = location.pathname;
    
    if (path.startsWith('/documents/') && params.documentId) {
      return {
        entityId: params.documentId,
        entityType: 'document' as const,
        isDocument: true
      };
    }
    
    if (path.startsWith('/library/') && params.itemId) {
      return {
        entityId: params.itemId,
        entityType: 'glossary' as const,
        isDocument: false
      };
    }
    
    if (path.startsWith('/templates/') && params.itemId) {
      return {
        entityId: params.itemId,
        entityType: 'blueprint' as const,
        isDocument: false
      };
    }
    
    return null;
  };

  const currentEntity = getCurrentEntity();

  // Get relationships for templates/library objects
  const { relationships, loading } = useLiveEntityRelationships(
    currentEntity?.entityId,
    currentEntity?.entityType,
    selectedGame?.id,
    false
  );

  useEffect(() => {
    // Subscribe to changes in highlighted items
    const unsubscribeTracker = highlightedItemsTracker.subscribe((items) => {
      setHighlightedItems(items);
    });

    // Subscribe to visibility changes
    const unsubscribeVisibility = highlightVisibilityService.subscribe(() => {
      setHiddenTypes(highlightVisibilityService.getHiddenTypes());
      setHiddenItems(highlightVisibilityService.getHiddenItems());
    });

    // Initialize hidden states
    setHiddenTypes(highlightVisibilityService.getHiddenTypes());
    setHiddenItems(highlightVisibilityService.getHiddenItems());

    return () => {
      unsubscribeTracker();
      unsubscribeVisibility();
    };
  }, []);

  const handleItemClick = (item: TrackedHighlightItem, event: React.MouseEvent) => {
    // Don't navigate if clicking the toggle button
    if ((event.target as HTMLElement).closest('.toggle-button')) {
      return;
    }

    const selectedGame = getSelectedGame();
    if (!selectedGame?.id) return;

    // Open item based on type and navigate to it
    if (item.type === 'glossary' || item.type === 'Library Object') {
      const path = `/library/${item.id}`;
      navigate(path);
    } else if (item.type === 'document') {
      const path = `/documents/${item.id}`;
      navigate(path);
    } else if (item.type === 'blueprint') {
      const path = `/templates/${item.id}`;
      navigate(path);
    }
  };

  // Get entity name based on type and id (for relationships view)
  const getEntityName = (id: string, type: string): string => {
    switch (type) {
      case 'glossary': {
        const item = objectsStore.getData()?.find(g => g.id === id);
        return item?.name || 'Unknown Item';
      }
      case 'document': {
        const doc = documentStore.getData()?.find(d => d.id === id);
        return doc?.name || 'Unknown Document';
      }
      case 'blueprint': {
        const bp = templatesStore.getData()?.find(b => b.id === id);
        return bp?.name || 'Unknown Blueprint';
      }
      default:
        return `Unknown ${type}`;
    }
  };

  // Get icon based on entity type (for relationships view)
  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'glossary':
        return <BookOpen className="h-4 w-4 text-purple-600 dark:text-purple-400" />;
      case 'document':
        return <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
      case 'blueprint':
        return <Layout className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'type':
        return <Type className="h-4 w-4 text-orange-600 dark:text-orange-400" />;
      default:
        return <BookOpen className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Navigate to entity detail page (for relationships view)
  const navigateToEntity = (id: string, type: string) => {
    switch (type) {
      case 'glossary':
        navigate(`/library/${id}`);
        break;
      case 'document':
        navigate(`/documents/${id}`);
        break;
      case 'blueprint':
        navigate(`/templates/${id}`);
        break;
      default:
        console.log(`Navigation not implemented for type: ${type}`);
    }
  };

  const handleToggleType = (type: string, event: React.MouseEvent) => {
    event.stopPropagation();
    highlightVisibilityService.toggleType(type);
  };

  const handleToggleItem = (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    highlightVisibilityService.toggleItem(itemId);
  };

  const getItemIcon = (type: string) => {
    const color = getEntityColor(type);
    switch (type) {
      case 'glossary':
        return <Library className="h-4 w-4" style={{ color }} />;
      case 'document':
        return <FileText className="h-4 w-4" style={{ color }} />;
      case 'blueprint':
        return <FileText className="h-4 w-4" style={{ color }} />;
      case 'text':
        return <Tag className="h-4 w-4" style={{ color }} />;
      case 'type':
      case 'type-value':
      case 'variable':
      case 'variable-value':
        return <Tag className="h-4 w-4" style={{ color }} />;
      default:
        return <Tag className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const groupedItems = highlightedItems.reduce((acc, item) => {
    const type = item.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(item);
    return acc;
  }, {} as Record<string, TrackedHighlightItem[]>);

  // Prepare relationships section for templates/objects/documents
  const hasReferencedBy = relationships?.referenced_by && relationships.referenced_by.length > 0;
  const hasReferences = relationships?.references && relationships.references.length > 0;
  const hasRelationships = hasReferencedBy || hasReferences;

  const relationshipsSection = currentEntity && !loading && relationships && hasRelationships && (
    <div className="space-y-4">
      {/* Referenced By / Used In Section */}
      {hasReferencedBy && (
        <div>
          <h5 className="text-xs font-medium text-foreground mb-2 flex items-center gap-2">
            <span>Used In</span>
            <span className="text-xs font-normal text-muted-foreground">({relationships.referenced_by_count})</span>
          </h5>
          <div className="space-y-1">
            {relationships.referenced_by.map((ref, index) => (
              <button
                key={`${ref.source_id}-${index}`}
                onClick={() => navigateToEntity(ref.source_id!, ref.source_type!)}
                className="w-full flex items-center justify-between p-2 rounded-md bg-card hover:bg-muted border border-border transition-colors group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {getEntityIcon(ref.source_type!)}
                  <span className="text-sm text-foreground truncate">
                    {getEntityName(ref.source_id!, ref.source_type!)}
                  </span>
                </div>
                <ChevronRight 
                  size={14} 
                  className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* References Section */}
      {hasReferences && (
        <div>
          <h5 className="text-xs font-medium text-foreground mb-2 flex items-center gap-2">
            <span>References</span>
            <span className="text-xs font-normal text-muted-foreground">({relationships.references_count})</span>
          </h5>
          <div className="space-y-1">
            {relationships.references.map((ref, index) => (
              <button
                key={`${ref.target_id}-${index}`}
                onClick={() => navigateToEntity(ref.target_id!, ref.target_type!)}
                className="w-full flex items-center justify-between p-2 rounded-md bg-card hover:bg-muted border border-border transition-colors group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {getEntityIcon(ref.target_type!)}
                  <span className="text-sm text-foreground truncate">
                    {getEntityName(ref.target_id!, ref.target_type!)}
                  </span>
                </div>
                <ChevronRight 
                  size={14} 
                  className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Prepare highlighted items section
  const hasHighlightedItems = highlightedItems.length > 0;
  
  const highlightedItemsSection = hasHighlightedItems && (
    <div className="space-y-3">
      {Object.entries(groupedItems).map(([type, items]) => {
        const isTypeHidden = hiddenTypes.has(type);
        return (
          <div key={type} className="space-y-0.5">
            {/* Type header with toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => handleToggleType(type, e)}
                className="flex items-center gap-2 flex-1 hover:bg-muted rounded px-1 py-1 transition-colors toggle-button"
                title={isTypeHidden ? `Show ${getEntityTypeLabel(type)}` : `Hide ${getEntityTypeLabel(type)}`}
              >
                {/* Colored dot indicator */}
                <div
                  className="w-2.5 h-2.5 rounded-full border-2 transition-all flex-shrink-0"
                  style={{
                    backgroundColor: isTypeHidden ? 'transparent' : getEntityColor(type),
                    borderColor: getEntityColor(type),
                    opacity: isTypeHidden ? 0.4 : 1
                  }}
                />
                <h4 className={`text-xs font-medium uppercase tracking-wide ${isTypeHidden ? 'text-muted-foreground line-through' : 'text-muted-foreground'}`}>
                  {getEntityTypeLabel(type)} ({items.length})
                </h4>
              </button>
            </div>

            {/* Items list (only show if type is not hidden) */}
            {!isTypeHidden && (
              <div className="space-y-0.5 pl-4">
                {items.map((item) => {
                  const isItemHidden = hiddenItems.has(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`w-full flex items-center gap-2 px-2 py-1 rounded transition-colors group hover:bg-muted ${isItemHidden ? 'opacity-50' : ''}`}
                    >
                      <button
                        onClick={(e) => handleItemClick(item, e)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <div className={`text-sm truncate text-foreground ${isItemHidden ? 'line-through' : ''}`}>
                          {item.name || item.keys[0]}
                        </div>
                        {item.keys.length > 1 && (
                          <div className="text-xs text-muted-foreground truncate">
                            Also: {item.keys.slice(1, 3).join(', ')}
                            {item.keys.length > 3 && ` +${item.keys.length - 3} more`}
                          </div>
                        )}
                      </button>
                      {/* Toggle button for individual item */}
                      <button
                        onClick={(e) => handleToggleItem(item.id, e)}
                        className="toggle-button opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-muted-foreground/20 rounded flex-shrink-0"
                        title={isItemHidden ? "Show highlights" : "Hide highlights"}
                      >
                        {isItemHidden ? (
                          <EyeOff className="h-3 w-3 text-muted-foreground" />
                        ) : (
                          <Eye className="h-3 w-3 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Show loading state
  if (currentEntity && loading) {
    return (
      <div className="flex flex-col h-full p-4">
        <div className="space-y-2 mt-2">
          <div className="h-10 bg-background rounded animate-pulse"></div>
          <div className="h-10 bg-background rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  // Combined view: Show both relationships and highlighted items
  const hasContent = hasRelationships || hasHighlightedItems;
  const totalConnections = (relationships?.referenced_by_count || 0) + (relationships?.references_count || 0);

  // Determine what to show in empty state based on context
  const getEmptyStateMessage = () => {
    if (currentEntity?.isDocument) {
      return {
        title: 'No Connections or Highlights',
        description: 'Items will appear here when this document references other content or contains highlighted terms.'
      };
    } else if (currentEntity) {
      return {
        title: 'No Connections Found',
        description: 'Connected items will appear here when this item references or is referenced by other objects.'
      };
    } else {
      return {
        title: 'No Content Selected',
        description: 'Open a document, template, or library item to view connections and highlighted content.'
      };
    }
  };

  const emptyState = getEmptyStateMessage();

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-1 overflow-y-auto p-4">
        {!hasContent ? (
          <div className="text-center text-muted-foreground text-sm">
            <p className="font-medium">{emptyState.title}</p>
            <p className="text-xs mt-2">
              {emptyState.description}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Relationships Section (Connections) */}
            {relationshipsSection && (
              <div>
                <div className="mb-3">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Connections
                  </h4>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Saved relationships with other items
                  </p>
                </div>
                {relationshipsSection}
              </div>
            )}
            
            {/* Separator if both sections exist */}
            {hasRelationships && hasHighlightedItems && (
              <div className="border-t border-border"></div>
            )}
            
            {/* Highlighted Items Section */}
            {highlightedItemsSection && (
              <div>
                <div className="mb-3">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {currentEntity?.isDocument ? 'In This Document' : 'Highlighted Items'}
                  </h4>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    {currentEntity?.isDocument 
                      ? 'Items referenced in the content'
                      : 'Items found in current content'}
                  </p>
                </div>
                {highlightedItemsSection}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with stats */}
      {hasContent && (
        <div className="p-3 border-t border-border bg-muted">
          <div className="text-xs text-muted-foreground space-y-1">
            {hasRelationships && (
              <div className="flex justify-between">
                <span>Connections</span>
                <span className="font-medium">{totalConnections}</span>
              </div>
            )}
            {hasHighlightedItems && (
              <div className="flex justify-between">
                <span>{currentEntity?.isDocument ? 'Referenced' : 'Highlighted'}</span>
                <span className="font-medium">{highlightedItems.length}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

